<template>
  <div
    v-if="editor"
    class="flex w-full flex-col gap-2 rounded-md border-[1px] border-gray-300 bg-neutral-100 p-2"
    :class="{ 'border-zuno-turquoise': editor.isFocused }"
    @click.stop="focusOnField"
  >
    <div class="px-2 text-sm min-h-10">
      <editor-content :editor="editor" />
      <!-- Placeholder for the suggestion dropdown -->
      <div
        v-if="showSuggestions"
        class="max-w-[360px] my-2 bg-white rounded-md shadow-md items"
      >
        <template v-if="showSuggestions && filteredUsers.length">
          <div class="flex flex-col p-4">
            <button
              v-for="(item, index) in filteredUsers"
              :key="index"
              class="flex items-start p-2 rounded-md hover:bg-gray-100 item gap-x-3 no-localize-translate"
              :class="{ 'is-selected': index === selectedIndex }"
              @click="selectItem(item)"
            >
              <div class="flex h-5 w-5 items-center justify-center rounded-full bg-[#218579] text-[0.5rem] text-white no-localize-translate">
                {{ getInitialsFromName(item.label) }}
              </div>
              {{ item.label }}
            </button>
          </div>
        </template>
        <div
          v-else
          class="p-3 item"
        >
          No result
        </div>
      </div>
    </div>
    <div class="flex justify-between">
      <div class="flex">
        <TipTapToggleButton
          :is-active="editor.isActive('bold')"
          @click="toggleBold"
        >
          <bold-icon custom-style="w-4 h-4" />
        </TipTapToggleButton>
        <TipTapToggleButton
          :is-active="editor.isActive('italic')"
          @click="toggleItalic"
        >
          <italic-icon custom-style="w-4 h-4" />
        </TipTapToggleButton>
        <TipTapToggleButton
          :is-active="editor.isActive('underline')"
          @click="toggleUnderline"
        >
          <underline-icon custom-style="w-4 h-4" />
        </TipTapToggleButton>
        <TipTapToggleButton
          :is-active="editor.isActive('bulletList')"
          @click="toggleBulletList"
        >
          <bullet-list-icon custom-style="w-4 h-4" />
        </TipTapToggleButton>
      </div>

      <!-- TipTapInput buttons - shows only when NOT in modal mode -->
      <div
        v-if="!isModalMode"
        class="flex gap-2"
      >
        <button
          v-if="alwaysShowCancelButton || !editor.isEmpty"
          class="px-3 py-1 text-sm font-normal rounded-md"
          :class="`${alwaysShowCancelButton || !editor.isEmpty ? 'border-[1px] border-[#218579]/10 bg-transparent text-[#218579] hover:border-[#218579]/10 hover:bg-[#218579]/10' : 'bg-gray-200 text-gray-300'}`"
          :disabled="editor.isEmpty && !alwaysShowCancelButton"
          @click.prevent="cancelButtonClicked"
        >
          Cancel
        </button>
        <button
          class="px-3 py-1 text-sm font-normal rounded-md"
          :class="`${!editor.isEmpty ? 'bg-[#218579]/10 text-[#11453f] hover:bg-[#218579]/25' : 'bg-gray-200 text-gray-300'}`"
          :disabled="editor.isEmpty"
          @click.stop="saveButtonClicked"
        >
          Save
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-3'
import Mention from '@tiptap/extension-mention'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Placeholder from '@tiptap/extension-placeholder'
import Link from '@tiptap/extension-link'
import BulletList from '@tiptap/extension-bullet-list'
import TipTapToggleButton from './tiptap-editor/TipTapToggleButton.vue'
// import { CustomMention } from './tiptap-editor/CustomMention'

export default {
  name: 'TipTapInput',

  components: {
    EditorContent,
    TipTapToggleButton,
  },

  props: {
    value: {
      type: String,
      default: '',
    },
    alwaysShowCancelButton: {
      type: Boolean,
      default: false,
    },
    autoFocus: {
      type: Boolean,
      default: false,
    },
    enableMentions: {
      type: Boolean,
      default: true,
    },
    mentionableUsers: {
      type: Array,
      default: () => [],
    },
    isModalMode: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['input', 'save-button-clicked', 'cancel-button-click', 'selected-ids'],

  data() {
    return {
      editor: null,
      component: null,
      // State for managing suggestion dropdown
      showSuggestions: false,
      filteredUsers: [],
      selectedIndex: 0,
      dropdownStyles: {
        top: '0px',
        left: '0px',
      },
      selectedUsers: [],
    }
  },

  computed: {
    isEmpty() {
      return this.editor?.isEmpty ?? true
    },
  },

  watch: {
    value(value) {
      // HTML
      const isSame = this.editor.getHTML() === value

      // JSON
      // const isSame = JSON.stringify(this.editor.getJSON()) === JSON.stringify(value)

      if (isSame) {
        return
      }

      this.editor.commands.setContent(value, false)
    },
  },

  mounted() {
    this.editor = new Editor({
      content: this.value,
      extensions: [
        StarterKit,
        Underline,
        BulletList,
        Placeholder.configure({
          emptyEditorClass: 'is-editor-empty',
          placeholder: 'Write a comment here, mention @user',
        }),
        this.enableMentions
        && Mention.configure({
          HTMLAttributes: {
            class: 'mention',
          },
          suggestion: {
            render: () => {
              return {
                onStart: (props) => {
                  this.filteredUsers = props.items
                  this.showSuggestions = true
                  this.selectedIndex = 0

                  // Position the dropdown
                  const { top, left } = props.clientRect()
                  this.dropdownStyles = {
                    top: `${top}px`,
                    left: `${left}px`,
                  }
                },
                onUpdate: (props) => {
                  this.filteredUsers = props.items

                  // Update the dropdown position
                  const { top, left } = props.clientRect()
                  this.dropdownStyles = {
                    top: `${top}px`,
                    left: `${left}px`,
                  }
                },
                onKeyDown: (props) => {
                  if (props.event.key === 'ArrowDown') {
                    this.selectedIndex = (this.selectedIndex + 1) % this.filteredUsers.length
                    return true
                  }

                  if (props.event.key === 'ArrowUp') {
                    this.selectedIndex = (this.selectedIndex - 1 + this.filteredUsers.length) % this.filteredUsers.length
                    return true
                  }

                  if (props.event.key === 'Enter') {
                    this.selectItem(this.filteredUsers[this.selectedIndex])
                    return true
                  }

                  return false
                },
                onExit: () => {
                  this.showSuggestions = false
                },
              }
            },
            items: ({ query }) => {
              return this.mentionableUsers
                .filter(item =>
                  item.label.toLowerCase().startsWith(query.toLowerCase()),
                )
                .slice(0, 5)
                .map(item => ({
                  id: item.id,
                  label: item.label,
                }))
            },
          },
        }),
        Link.configure({
          openOnClick: false,
        }),
      ],
      onUpdate: () => {
        // HTML
        this.$emit('input', this.editor.getHTML())
        this.updateSelectedUsers() // Listen for editor changes
      },
    })
    if (this.autoFocus && this.editor) {
      this.editor.commands.focus('end')
    }
  },

  beforeUnmount() {
    if (this.editor) {
      this.editor.destroy()
    }
  },

  methods: {
    saveButtonClicked() {
      this.$emit('selected-ids', this.selectedUsers)
      this.$emit('save-button-clicked', this.editor.getHTML())
      // clear the field
      this.editor.chain().focus().setContent('').run()
      this.selectedUsers = []
    },
    cancelButtonClicked() {
      // this.editor.chain().focus().setContent('').run()
      this.$emit('cancel-button-click', '')
      this.editor.destroy()
      // this.$emit('input', this.editor.getHTML())
    },
    focusOnField() {
      this.editor.chain().focus().run()
    },
    toggleBold() {
      this.editor.chain().focus().toggleBold().run()
    },
    toggleItalic() {
      this.editor.chain().focus().toggleItalic().run()
    },
    toggleUnderline() {
      this.editor.chain().focus().toggleUnderline().run()
    },
    toggleBulletList() {
      this.editor.chain().focus().toggleBulletList().run()
    },

    selectItem(user) {
      this.removeLastWord()
      this.editor.chain().focus().insertContent(` @${user.label}`).run()
      this.updateSelectedUsers()
      this.showSuggestions = false
    },

    removeLastWord() {
      const content = this.editor.getHTML()
      const words = content.trim().split(' ')
      words.pop()
      this.editor.chain().focus().setContent('').run()
      this.editor.chain().focus().insertContent(words.join(' ')).run()
    },

    extractMentions(content) {
      const mentionRegex = /@([A-Za-z]+(?: [A-Za-z]+)?)/g
      const mentions = [...content.matchAll(mentionRegex)].map(match => match[1])
      return mentions
    },

    // Function to update selected user IDs based on editor content
    updateSelectedUsers() {
      const content = this.editor.getHTML()
      const mentionedNames = this.extractMentions(content)
      const userIds = this.mentionableUsers.filter((user) => {
        const nameParts = user.label.split(' ') // Split the user's name into parts
        const firstAndSecondName = nameParts.slice(0, 2).join(' ')
        return mentionedNames.includes(firstAndSecondName)
      }).map(user => user.id)
      this.selectedUsers = userIds
    },

  },
}
</script>

<style>
.tiptap p.is-editor-empty:first-child::before {
  color: #adb5bd;
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
}

.tiptap>*+* {
  margin-top: 0.75em;
}

.tiptap ul,
.tiptap ol {
  padding: 0 1rem;
}

.ProseMirror:focus {
  outline: none;
}

.mention {
  border-radius: 0.4rem;
  padding: 0.1rem 0.3rem;
  color: #218579;
  background-color: #e9f3f2;
}

.tiptap a {
  color: #68cef8;
}

.tiptap a:hover {
  background-color: #e9f3f2;
}

.tiptap ul,
.tiptap ol {
  list-style: revert;
}
</style>
